import type { Cell, Header, HeaderGroup, Row } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import { X } from 'lucide-react'
import { memo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/use-translation'
import { DataTableFilters } from '../data-table-filters'
import { DataTablePagination } from '../data-table-pagination'
import { DataTableViewOptions } from '../data-table-view-options'
import type { DataTableContentProps } from '../types/data-table.types'

const EMPTY_COLUMN_VISIBILITY: Record<string, boolean> = {}

function DataTableContentComponent<TData, TValue = unknown>({
  table,
  tableContainerClassName,
  columns,
  searchKey,
  searchPlaceholder,
  pageSizeOptions,
  onSearchChange,
  searchValue,
  filters,
  onFiltersChange,
  columnVisibility = EMPTY_COLUMN_VISIBILITY,
  onColumnVisibilityChange,
  isFetching = false,
  isLoading = false,
  onRowClick,
  pagination,
}: DataTableContentProps<TData, TValue>) {
  const t = useTranslation()

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center space-x-3">
          {searchKey && (
            <div className="relative max-w-sm">
              <Input
                placeholder={searchPlaceholder || t('dataTable.searchPlaceholder')}
                value={searchValue || ''}
                onChange={(event) => {
                  onSearchChange(event.target.value)
                }}
                className="pr-8 h-8 text-xs"
              />
              {searchValue && searchValue.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSearchChange('')}
                  className="absolute right-0 top-0 h-full px-2 py-1 hover:bg-transparent"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
          <DataTableFilters
            table={table}
            columns={table.getAllColumns()}
            excludeSearchKey={searchKey}
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
        </div>
        <DataTableViewOptions
          table={table}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
        />
      </div>
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border border-border bg-card backdrop-blur-sm shadow-md [&_[data-slot=table-container]]:!overflow-x-visible',
          tableContainerClassName,
        )}
      >
        {(isLoading || isFetching) && (
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span>{isLoading ? t('common.loading') : t('common.refreshing')}</span>
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => (
              <TableRow key={headerGroup.id} className="border-b border-border bg-muted">
                {headerGroup.headers.map((header: Header<TData, unknown>) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={`h-8 px-4 py-1 text-xs font-medium text-muted-foreground ${header.id === 'actions' ? 'w-20' : ''}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row: Row<TData>) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={`border-b border-border transition-all duration-200 ease-smooth hover:bg-card-hover ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell: Cell<TData, unknown>) => (
                    <TableCell
                      key={cell.id}
                      className={`px-4 py-3 text-xs text-foreground ${cell.column.id === 'actions' ? 'w-20' : ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground text-xs bg-muted/30"
                >
                  {t('dataTable.noResultsFound')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="px-1">
        <DataTablePagination
          table={table}
          pageSizeOptions={pageSizeOptions}
          pagination={pagination}
        />
      </div>
    </div>
  )
}

export const DataTableContent = memo(DataTableContentComponent) as typeof DataTableContentComponent
